<template>
  <layout-basic>
    <page-hero
      title="Helping Growing Businesses Build Better Workplaces Through Learning"
      lead="At IMPROVE, we believe workplace learning should be simple, engaging and accessible for every organisation."
    />

    <section class="about-intro">
      <v-container>
        <div class="about-intro__copy">
          <p>
            That’s why we’ve created a learning platform that helps growing
            businesses onboard new employees, stay compliant and develop their
            people—without the complexity often associated with traditional
            learning systems.
          </p>
          <p>
            Whether you’re training five people or two hundred and fifty, our
            goal is the same: to make learning easier for everyone.
          </p>
        </div>
      </v-container>
    </section>

    <section class="demo-video-section">
      <v-container style="max-width: 960px">
        <demo-video video-key="about" />
      </v-container>
    </section>

    <section class="about-why">
      <v-container>
        <h2 class="font-sora about-section-title">Why IMPROVE?</h2>
        <div class="about-why__copy">
          <p>
            IMPROVE was founded by learning and development specialist Steve
            Walker, who has spent more than 30 years helping organisations
            improve performance through learning.
          </p>
          <p>
            After working with thousands of businesses, one challenge kept
            appearing. Learning platforms had become increasingly complicated,
            expensive and difficult to manage—particularly for growing
            organisations.
          </p>
          <p>IMPROVE was created to change that.</p>
          <p>
            Today, we’re proud to support businesses across the UK with a
            platform that’s simple to use, affordable to own and designed around
            real business challenges.
          </p>
        </div>
      </v-container>
    </section>

    <section class="about-beliefs">
      <v-container>
        <h2 class="font-sora about-section-title">What We Believe</h2>
        <div class="about-beliefs__grid">
          <div
            v-for="belief in beliefs"
            :key="belief.title"
            class="about-beliefs__card"
          >
            <strong>{{ belief.title }}</strong>
            <span>{{ belief.body }}</span>
          </div>
        </div>
      </v-container>
    </section>

    <section class="about-promise">
      <v-container>
        <h2 class="font-sora about-section-title">Our Promise</h2>
        <div class="about-promise__copy">
          <p>
            We’re committed to helping every customer create a better learning
            experience for their people.
          </p>
          <p>
            That means delivering a platform that’s easy to use, backed by
            responsive support and continually evolving to meet the needs of
            growing businesses.
          </p>
          <p class="about-promise__remember">
            If there’s one thing we’d like you to remember…
          </p>
          <p class="about-promise__highlight">
            IMPROVE isn’t just another learning platform. It’s a simpler way to
            help your people learn, grow and succeed.
          </p>
        </div>
      </v-container>
    </section>

    <standard-cta
      title="Ready to See IMPROVE in Action?"
      lead="Whether you’re exploring learning platforms for the first time or looking for a better alternative, we’d love to show you how IMPROVE can help."
    />
  </layout-basic>
</template>

<script>
import LayoutBasic from '@/layouts/Dashboard';
import PageHero from '@/components/marketing/PageHero.vue';
import StandardCta from '@/components/marketing/StandardCta.vue';
import DemoVideo from '@/components/DemoVideo.vue';
import { marketingPageMeta } from '@/utils/marketingPage';

export default {
  name: 'About',
  components: {
    LayoutBasic,
    PageHero,
    StandardCta,
    DemoVideo,
  },
  metaInfo() {
    return marketingPageMeta({
      title: 'Our Story | IMPROVE',
      description:
        'Helping growing businesses build better workplaces through learning. Meet the team behind IMPROVE and learn why we built a simpler platform for SMEs.',
      path: '/about-us',
    });
  },
  data() {
    return {
      beliefs: [
        {
          title: 'Learning should be simple.',
          body:
            'Technology should make developing people easier, not harder.',
        },
        {
          title: 'Every employee deserves the opportunity to learn.',
          body:
            'From day one through to leadership development, everyone should have access to engaging learning.',
        },
        {
          title: 'Great businesses are built by great managers.',
          body:
            'That’s why we’ve invested in practical management development through Leadership Plus.',
        },
        {
          title: 'Relationships matter.',
          body:
            'We’re here to build long-term partnerships, supporting our customers as they grow.',
        },
      ],
    };
  },
};
</script>

<style scoped>
.about-intro,
.about-why,
.about-promise {
  padding: 8px 0 32px;
}

.about-intro__copy,
.about-why__copy,
.about-promise__copy {
  max-width: 720px;
  margin: 0 auto;
  color: var(--brand-navy, #002458);
  font-size: 17px;
  line-height: 1.6;
}

.about-intro__copy p,
.about-why__copy p,
.about-promise__copy p {
  margin: 0 0 16px;
}

.about-section-title {
  text-align: center;
  color: var(--brand-navy, #002458);
  font-size: 28px;
  font-weight: 700;
  margin: 0 0 24px;
}

.about-beliefs {
  padding: 40px 0;
  background: #f7f9fb;
}

.about-beliefs__grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
  max-width: 900px;
  margin: 0 auto;
}

.about-beliefs__card {
  display: flex;
  flex-direction: column;
  gap: 8px;
  background: #fff;
  border: 1px solid #e6ebf1;
  border-radius: 12px;
  padding: 22px 18px;
}

.about-beliefs__card strong {
  color: var(--brand-navy, #002458);
  font-size: 17px;
}

.about-beliefs__card span {
  color: var(--brand-grey, #6b7280);
  font-size: 15px;
  line-height: 1.5;
}

.about-promise__remember {
  font-weight: 600;
  margin-top: 8px !important;
}

.about-promise__highlight {
  color: var(--brand-teal, #08a0a8) !important;
  font-weight: 700;
  font-size: 18px;
}

@media screen and (max-width: 700px) {
  .about-beliefs__grid {
    grid-template-columns: 1fr;
  }
}
</style>
